'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	ImageIcon,
	PaletteIcon,
	ScanLineIcon,
	Settings2Icon,
	TrashIcon,
	UploadIcon,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { TQrCodeOptions, TColorOrGradient } from '@shared/schemas';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/components/ui/accordion';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { FileUpload, FileUploadDropzone } from '@/components/ui/file-upload';
import { useQrCodeGeneratorStore } from '@/components/provider/QrCodeConfigStoreProvider';
import { ColorPicker } from './ColorPicker';
import IconPicker from './IconPicker';
import { useToast } from '@/components/ui/use-toast';

// Constants
const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
const ACCEPTED_FILE_TYPES = '.jpg,.jpeg,.png,.svg,.webp';
const SIZE_CONFIG = {
	MIN: 300,
	MAX: 2000,
	STEP: 100,
} as const;
const MARGIN_CONFIG = {
	MIN: 0,
	MAX: 10,
	STEP: 1,
} as const;

export const SettingsForm = () => {
	const t = useTranslations('generator.settingsForm');
	const { toast } = useToast();
	const { config, updateConfig } = useQrCodeGeneratorStore((state) => state);
	const [imageDisplayName, setImageDisplayName] = useState<string | undefined>();
	const [uploadResetKey, setUploadResetKey] = useState(0);
	const lastOpaqueBackground = useRef<TColorOrGradient>(config.backgroundOptions.style);
	const [transparentBackground, setTransparentBackground] = useState(
		config.backgroundOptions.style.type === 'rgba' &&
			/,\s*0\s*\)$/.test(config.backgroundOptions.style.value),
	);

	// Memoize default values to prevent unnecessary re-renders
	const defaultValues = useMemo<TQrCodeOptions>(
		() => ({
			width: config.width,
			height: config.height,
			margin: config.width > 0 ? Math.round((config.margin / config.width) * 100) : 0,
			dotsOptions: {
				type: config.dotsOptions.type,
				style: config.dotsOptions.style,
			},
			cornersSquareOptions: {
				type: config.cornersSquareOptions.type,
				style: config.cornersSquareOptions.style,
			},
			cornersDotOptions: {
				type: config.cornersDotOptions.type,
				style: config.cornersDotOptions.style,
			},
			backgroundOptions: {
				style: config.backgroundOptions.style,
			},
			frameOptions: config.frameOptions,
			imageOptions: {
				hideBackgroundDots: config.imageOptions?.hideBackgroundDots ?? true,
			},
		}),
		[config],
	);

	const form = useForm<TQrCodeOptions>({
		defaultValues,
	});

	// Memoized handler to update config immutably
	const handleChange = useCallback(
		(data: TQrCodeOptions) => {
			const updatedConfig = {
				...config,
				width: Number(data.width),
				height: Number(data.height),
				margin: Math.round((Number(data.width) / 100) * data.margin),
				dotsOptions: {
					type: data.dotsOptions.type,
					style: data.dotsOptions.style,
				},
				cornersSquareOptions: {
					type: data.cornersSquareOptions.type,
					style: data.cornersSquareOptions.style,
				},
				cornersDotOptions: {
					type: data.cornersDotOptions.type,
					style: data.cornersDotOptions.style,
				},
				backgroundOptions: {
					style: data.backgroundOptions.style,
				},
				frameOptions: data.frameOptions,
				imageOptions: {
					hideBackgroundDots: data.imageOptions.hideBackgroundDots,
				},
			};
			updateConfig(updatedConfig);
		},
		[config, updateConfig],
	);

	// Memoized icon selection handler
	const handleIconSelect = useCallback(
		(iconBase64?: string, iconName?: string) => {
			const updatedConfig = {
				...config,
				image: iconBase64,
			};
			updateConfig(updatedConfig);
			setImageDisplayName(iconName);
			form.setValue('image', iconBase64 ?? '');
			setUploadResetKey((prev) => prev + 1);
		},
		[config, updateConfig, form],
	);

	// Memoized icon removal handler
	const handleRemoveIcon = useCallback(() => {
		const updatedConfig = {
			...config,
			image: '',
		};
		updateConfig(updatedConfig);
		setImageDisplayName(undefined);
		setUploadResetKey((prev) => prev + 1);
	}, [config, updateConfig]);

	// Handler for FileUpload component accepted files
	const handleFileAccept = useCallback(
		(files: File[]) => {
			const file = files[0];
			if (!file) return;

			const reader = new FileReader();

			reader.onload = () => {
				const base64 = reader.result as string;
				updateConfig({ image: base64 });
				setImageDisplayName(file.name);
				setUploadResetKey((prev) => prev + 1);
			};

			reader.onerror = () => {
				toast({
					variant: 'destructive',
					title: t('errorToLargeFile'),
				});
			};

			reader.readAsDataURL(file);
		},
		[updateConfig, t, toast],
	);

	// Handler for FileUpload component rejected files
	const handleFileReject = useCallback(
		(_file: File, message: string) => {
			toast({
				variant: 'destructive',
				title: message,
				description: t('uploadFormats'),
			});
		},
		[toast, t],
	);

	// Memoized size change handler
	const handleSizeChange = useCallback(
		(value: number[]) => {
			const newSize = value[0];
			if (newSize !== undefined) {
				form.setValue('width', newSize);
				form.setValue('height', newSize);
				updateConfig({ width: newSize, height: newSize });
			}
		},
		[form, updateConfig],
	);

	// Memoized color change handler factory
	const createColorChangeHandler = useCallback(
		(fieldOnChange: (value: TColorOrGradient) => void) => (color: TColorOrGradient) => {
			fieldOnChange(color);
			handleChange(form.getValues());
		},
		[form, handleChange],
	);

	return (
		<Form {...form}>
			<form onChange={form.handleSubmit(handleChange)} className="w-full space-y-4">
				<Accordion type="multiple" defaultValue={['frame']} className="space-y-4">
					<AccordionItem
						value="general"
						className="rounded-2xl border border-slate-200 bg-white px-5"
					>
						<AccordionTrigger className="py-5 hover:no-underline">
							<span className="flex items-center gap-3 text-left">
								<span className="grid size-10 place-items-center rounded-lg bg-slate-100">
									<Settings2Icon className="size-5" />
								</span>
								<span>
									<span className="block font-semibold">General</span>
									<span className="block text-sm font-normal text-slate-500">
										Size, quiet zone, and background
									</span>
								</span>
							</span>
						</AccordionTrigger>
						<AccordionContent className="pb-5">
							<div className="flex flex-col flex-wrap space-y-6 p-2">
								<FormField
									control={form.control}
									name="width"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('sizeLabel')}</FormLabel>
											<FormControl>
												<Slider
													className="cursor-pointer"
													value={[field.value]}
													max={SIZE_CONFIG.MAX}
													min={SIZE_CONFIG.MIN}
													step={SIZE_CONFIG.STEP}
													onValueChange={handleSizeChange}
												/>
											</FormControl>
											<FormDescription className="pt-1 text-center">
												{form.getValues('height')} x {form.getValues('width')} px
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="margin"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('borderSpacingLabel')}</FormLabel>
											<FormControl>
												<Slider
													className="cursor-pointer"
													value={[field.value]}
													max={MARGIN_CONFIG.MAX}
													min={MARGIN_CONFIG.MIN}
													step={MARGIN_CONFIG.STEP}
													onValueChange={(values) => {
														field.onChange(values[0]);
														const percentage = values[0] ?? 0;
														updateConfig({ margin: Math.round((config.width / 100) * percentage) });
													}}
												/>
											</FormControl>
											<FormDescription className="pt-1 text-center">
												{field.value} %
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="backgroundOptions.style"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('backgroundLabel')}</FormLabel>
											<label className="mb-3 flex items-center gap-2 text-sm font-normal">
												<Checkbox
													checked={transparentBackground}
													onCheckedChange={(checked) => {
														const nextTransparent = checked === true;
														if (!nextTransparent) {
															const current = config.backgroundOptions.style;
															if (!(current.type === 'rgba' && /,\s*0\s*\)$/.test(current.value))) {
																lastOpaqueBackground.current = current;
															}
														}
														const next = nextTransparent
															? { type: 'rgba' as const, value: 'rgba(255, 255, 255, 0)' }
															: lastOpaqueBackground.current;
														setTransparentBackground(nextTransparent);
														field.onChange(next);
														updateConfig({ backgroundOptions: { style: next } });
													}}
												/>
												<span>Transparent background</span>
											</label>
											<FormControl>
												{transparentBackground ? (
													<p className="rounded-md border border-dashed px-3 py-2 text-sm text-slate-500">
														QR code background exports as transparent.
													</p>
												) : (
													<ColorPicker
														defaultColor={field.value}
														onChange={(color) => {
															lastOpaqueBackground.current = color;
															createColorChangeHandler(field.onChange)(color);
														}}
													/>
												)}
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="pattern"
						className="rounded-2xl border border-slate-200 bg-white px-5"
					>
						<AccordionTrigger className="py-5 hover:no-underline">
							<span className="flex items-center gap-3 text-left">
								<span className="grid size-10 place-items-center rounded-lg bg-slate-100">
									<PaletteIcon className="size-5" />
								</span>
								<span>
									<span className="block font-semibold">QR code pattern and corners</span>
									<span className="block text-sm font-normal text-slate-500">
										Choose dot and finder styles, then set their colors
									</span>
								</span>
							</span>
						</AccordionTrigger>
						<AccordionContent className="pb-5">
							<div className="flex flex-col flex-wrap space-y-6 p-2">
								{/* Dots Options */}
								<div className="block w-full flex-wrap space-y-2 sm:flex sm:space-y-0 sm:space-x-8">
									<FormField
										control={form.control}
										name="dotsOptions.type"
										render={({ field }) => (
											<FormItem className="flex-1">
												<FormLabel>{t('dotStyle.label')}</FormLabel>
												<FormControl>
													<Select
														onValueChange={(value) => {
															field.onChange(value);
															updateConfig({
																dotsOptions: {
																	...config.dotsOptions,
																	type: value as typeof config.dotsOptions.type,
																},
															});
														}}
														value={field.value}
													>
														<SelectTrigger>
															<SelectValue placeholder={t('dotStyle.placeholder')} />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value="square">
																{t('dotStyle.optionLabelSquare')}
															</SelectItem>
															<SelectItem value="dots">{t('dotStyle.optionLabelDots')}</SelectItem>
															<SelectItem value="rounded">
																{t('dotStyle.optionLabelRounded')}
															</SelectItem>
															<SelectItem value="extra-rounded">
																{t('dotStyle.optionLabelExtraRound')}
															</SelectItem>
															<SelectItem value="classy">
																{t('dotStyle.optionLabelClassy')}
															</SelectItem>
															<SelectItem value="classy-rounded">
																{t('dotStyle.optionLabelClassyRounded')}
															</SelectItem>
														</SelectContent>
													</Select>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="dotsOptions.style"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t('dotColor')}</FormLabel>
												<FormControl>
													<ColorPicker
														defaultColor={field.value}
														onChange={createColorChangeHandler(field.onChange)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								{/* Corners Square Options */}
								<div className="block w-full flex-wrap space-y-2 sm:flex sm:space-y-0 sm:space-x-8">
									<FormField
										control={form.control}
										name="cornersSquareOptions.type"
										render={({ field }) => (
											<FormItem className="flex-1">
												<FormLabel>{t('cornersSquareOptions.label')}</FormLabel>
												<FormControl>
													<Select
														onValueChange={(value) => {
															field.onChange(value);
															updateConfig({
																cornersSquareOptions: {
																	...config.cornersSquareOptions,
																	type: value as typeof config.cornersSquareOptions.type,
																},
															});
														}}
														value={field.value}
													>
														<SelectTrigger>
															<SelectValue placeholder={t('cornersSquareOptions.placeholder')} />
														</SelectTrigger>
														<SelectContent>
															<SelectItem icon="icons/corners-square-square.svg" value="square">
																{t('cornersSquareOptions.optionLabelSquare')}
															</SelectItem>
															<SelectItem icon="icons/corners-square-dot.svg" value="dot">
																{t('cornersSquareOptions.optionLabelDot')}
															</SelectItem>
															<SelectItem
																icon="icons/corners-square-rounded.svg"
																value="extra-rounded"
															>
																{t('cornersSquareOptions.optionLabelExtraRound')}
															</SelectItem>
														</SelectContent>
													</Select>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="cornersSquareOptions.style"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t('cornersSquareColor')}</FormLabel>
												<FormControl>
													<ColorPicker
														defaultColor={field.value}
														onChange={createColorChangeHandler(field.onChange)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								{/* Corners Dot Options */}
								<div className="block w-full flex-wrap space-y-2 sm:flex sm:space-y-0 sm:space-x-8">
									<FormField
										control={form.control}
										name="cornersDotOptions.type"
										render={({ field }) => (
											<FormItem className="flex-1">
												<FormLabel>{t('cornersDotOptions.label')}</FormLabel>
												<FormControl>
													<Select
														onValueChange={(value) => {
															field.onChange(value);
															updateConfig({
																cornersDotOptions: {
																	...config.cornersDotOptions,
																	type: value as typeof config.cornersDotOptions.type,
																},
															});
														}}
														value={field.value}
													>
														<SelectTrigger>
															<SelectValue placeholder={t('cornersDotOptions.placeholder')} />
														</SelectTrigger>
														<SelectContent>
															<SelectItem icon="icons/corners-dot-square.svg" value="square">
																{t('cornersDotOptions.optionLabelSquare')}
															</SelectItem>
															<SelectItem icon="icons/corners-dot-dot.svg" value="dot">
																{t('cornersDotOptions.optionLabelDot')}
															</SelectItem>
														</SelectContent>
													</Select>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="cornersDotOptions.style"
										render={({ field }) => (
											<FormItem>
												<FormLabel>{t('cornersDotColor')}</FormLabel>
												<FormControl>
													<ColorPicker
														defaultColor={field.value}
														onChange={createColorChangeHandler(field.onChange)}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</div>
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value="logo" className="rounded-2xl border border-slate-200 bg-white px-5">
						<AccordionTrigger className="py-5 hover:no-underline">
							<span className="flex items-center gap-3 text-left">
								<span className="grid size-10 place-items-center rounded-lg bg-slate-100">
									<ImageIcon className="size-5" />
								</span>
								<span>
									<span className="block font-semibold">Add logo</span>
									<span className="block text-sm font-normal text-slate-500">
										Upload a logo or choose a built-in icon
									</span>
								</span>
							</span>
						</AccordionTrigger>
						<AccordionContent className="pb-5">
							<div className="flex flex-col flex-wrap space-y-6 p-2">
								{config.image ? (
									<div className="flex items-center gap-4 rounded-lg border p-4">
										{/* eslint-disable-next-line @next/next/no-img-element */}
										<img
											src={config.image}
											alt={t('iconLabel')}
											className="size-14 shrink-0 rounded-md border object-contain bg-accent/50 p-1"
										/>
										<div className="min-w-0 flex-1">
											<p className="text-sm font-medium">{t('currentIcon')}</p>
											<p className="truncate text-xs text-muted-foreground">
												{imageDisplayName ?? t('iconLabel')}
											</p>
										</div>
										<Button
											type="button"
											variant="ghost"
											size="icon"
											onClick={handleRemoveIcon}
											aria-label={t('clearBtn')}
										>
											<TrashIcon className="size-4 text-destructive" />
										</Button>
									</div>
								) : null}

								<FormField
									control={form.control}
									name="image"
									render={() => (
										<FormItem>
											<FormLabel>{t('iconLabel')}</FormLabel>
											<FormControl>
												<FileUpload
													key={uploadResetKey}
													accept={ACCEPTED_FILE_TYPES}
													maxSize={MAX_FILE_SIZE}
													maxFiles={1}
													onAccept={handleFileAccept}
													onFileReject={handleFileReject}
												>
													<FileUploadDropzone className="min-h-[120px] cursor-pointer flex-col gap-2">
														<UploadIcon className="size-6 text-muted-foreground" />
														<div className="text-center">
															<p className="text-sm font-medium">{t('uploadDragDrop')}</p>
															<p className="text-xs text-muted-foreground">{t('uploadFormats')}</p>
														</div>
													</FileUploadDropzone>
												</FileUpload>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="imageOptions.hideBackgroundDots"
									render={({ field }) => (
										<FormItem className="flex flex-row items-start space-y-0 space-x-3 rounded-md border p-4">
											<FormLabel className="cursor-pointer">{t('hideBackgroundDots')}</FormLabel>
											<FormControl>
												<Checkbox checked={field.value} onCheckedChange={field.onChange} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<div>
									<p className="mb-4 text-sm leading-none font-medium">{t('predefinedIconInfo')}</p>
									<IconPicker onSelect={handleIconSelect} />
								</div>
							</div>
						</AccordionContent>
					</AccordionItem>

					<AccordionItem
						value="frame"
						className="rounded-2xl border border-slate-200 bg-white px-5"
					>
						<AccordionTrigger className="py-5 hover:no-underline">
							<span className="flex items-center gap-3 text-left">
								<span className="grid size-10 place-items-center rounded-lg bg-slate-100">
									<ScanLineIcon className="size-5" />
								</span>
								<span>
									<span className="block font-semibold">Frame</span>
									<span className="block text-sm font-normal text-slate-500">
										Add a visible prompt that encourages a scan
									</span>
								</span>
							</span>
						</AccordionTrigger>
						<AccordionContent className="pb-5">
							<div className="space-y-4 p-2">
								<div>
									<h3 className="font-medium">Frame style</h3>
									<p className="text-sm text-muted-foreground">
										Add a clear scan prompt around your code. You can edit the caption below.
									</p>
								</div>
								<FormField
									control={form.control}
									name="frameOptions.style"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Choose a frame</FormLabel>
											<div
												className="grid grid-cols-2 gap-3 sm:grid-cols-4"
												role="radiogroup"
												aria-label="Choose a frame"
											>
												{(
													[
														['none', 'No frame'],
														['label', 'Caption below'],
														['badge', 'Scan badge'],
														['outline', 'Outlined'],
													] as const
												).map(([style, label]) => {
													const selected = field.value === style;
													return (
														<button
															key={style}
															type="button"
															role="radio"
															aria-checked={selected}
															onClick={() => {
																field.onChange(style);
																updateConfig({
																	frameOptions: { ...form.getValues('frameOptions'), style },
																});
															}}
															className={`rounded-xl border p-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 ${selected ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-400'}`}
														>
															<span
																className={`mb-2 flex h-24 flex-col items-center justify-center rounded-md bg-slate-50 p-2 ${style === 'outline' ? 'border-2 border-slate-900' : ''}`}
															>
																<span
																	aria-hidden="true"
																	className="block size-12 shrink-0 border-[5px] border-slate-900 bg-[repeating-conic-gradient(#0f172a_0_25%,transparent_0_50%)] bg-[length:8px_8px] shadow-[inset_0_0_0_4px_white]"
																/>
																{style === 'label' && (
																	<span className="mt-1 block text-center text-[8px] font-bold uppercase tracking-wide">
																		SCAN ME
																	</span>
																)}
																{style === 'badge' && (
																	<span className="mt-1 block w-16 rounded bg-slate-900 py-1 text-center text-[8px] font-bold text-white">
																		SCAN ME
																	</span>
																)}
															</span>
															<span className="block text-xs font-medium">{label}</span>
														</button>
													);
												})}
											</div>
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="frameOptions.text"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Caption</FormLabel>
											<FormControl>
												<input
													{...field}
													maxLength={24}
													aria-label="QR frame caption"
													onChange={(event) => {
														field.onChange(event);
														updateConfig({
															frameOptions: {
																...form.getValues('frameOptions'),
																text: event.target.value,
															},
														});
													}}
													className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
												/>
											</FormControl>
											<FormDescription>Up to 24 characters.</FormDescription>
										</FormItem>
									)}
								/>
							</div>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</form>
		</Form>
	);
};
